import React, {
  memo,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import {
  deleteInventoryReport,
  editHarvestSummary,
  editHarvestSummaryStatus,
  manualRefreshTriggerById,
} from 'api/reports';
import { useMutation, useQueryClient } from 'react-query';

import {
  RefType,
  EditHarvestSummaryModalProps,
} from './EditHarvestSummaryModal.props';
import { InventoryReportStatusEnum, Report } from 'types/report';

import EditHarvestSummaryModalView from './EditHarvestSummaryModal.view';

const Container = forwardRef<RefType, EditHarvestSummaryModalProps>(
  (props, ref) => {
    const toast = useToast();
    const queryClient = useQueryClient();

    const [report, setReport] = useState<Report | null>(null);

    const { isOpen, onOpen, onClose } = useDisclosure();
    useImperativeHandle(
      ref,
      () => ({
        onOpen: (_report) => {
          setReport(_report);

          onOpen();
        },
      }),
      [],
    );

    useEffect(() => {
      if (report?.details) {
        report.details.sort((a, b) => {
          const nameA = a.sku.name.toUpperCase();
          const nameB = b.sku.name.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0; // names must be equal
        });
      }
    }, [report]);

    const { mutate: editInventory, isLoading } = useMutation(
      editHarvestSummary,
      {
        onSuccess: (response) => {
          toast({
            status: 'success',
            title: 'Success',
            description: 'Harvest Summary Updated',
            isClosable: true,
            duration: 5000,
          });

          // Close modal first to allow queries to refetch
          onClose();

          // Invalidate and refetch queries to get fresh data
          if (report?._id) {
            // Invalidate the specific report query
            queryClient.invalidateQueries({
              queryKey: ['fetch-reports', report._id],
            });
          }

          // Invalidate all related queries to ensure fresh data
          queryClient.invalidateQueries({
            queryKey: ['fetch-reports'],
          });

          queryClient.invalidateQueries({
            queryKey: ['fetch-inventory-reports'],
          });

          // Also invalidate any queries that might contain the specific report
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.includes('fetch-reports') ||
              query.queryKey.includes('fetch-inventory-reports') ||
              query.queryKey.includes('fetch-impact-reports'),
          });

          // Force refetch the specific report data if it's cached
          if (report?._id) {
            queryClient.invalidateQueries({
              queryKey: ['fetch-reports', report._id],
            });
          }

          // Force refetch the report list with all possible query key variations
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey.length > 0 &&
                queryKey[0] === 'fetch-reports'
              );
            },
          });

          // Invalidate activity history queries for this specific report
          if (report?._id) {
            queryClient.invalidateQueries({
              queryKey: [`fetch-report-activity-history-${report._id}`],
            });
          }
        },
        onError: (error) => {
          console.error('Error updating harvest summary:', error);
          toast({
            status: 'error',
            title: 'Error',
            description: 'Failed to update Harvest Summary',
            isClosable: true,
            duration: 5000,
          });
        },
      },
    );

    const { mutate: editReportStatus, isLoading: isUpdating } = useMutation(
      editHarvestSummaryStatus,
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: 'Success',
            description: 'Harvest Summary Status Updated',
            isClosable: true,
            duration: 5000,
          });

          // Invalidate all related queries to ensure fresh data
          queryClient.invalidateQueries({
            queryKey: ['fetch-reports'],
          });

          queryClient.invalidateQueries({
            queryKey: ['fetch-inventory-reports'],
          });

          // Also invalidate any queries that might contain the specific report
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.includes('fetch-reports') ||
              query.queryKey.includes('fetch-inventory-reports') ||
              query.queryKey.includes('fetch-impact-reports'),
          });

          // Force refetch the specific report data if it's cached
          if (report?._id) {
            queryClient.invalidateQueries({
              queryKey: ['fetch-reports', report._id],
            });
          }

          // Force refetch the report list with all possible query key variations
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey.length > 0 &&
                queryKey[0] === 'fetch-reports'
              );
            },
          });

          // Invalidate activity history queries for this specific report
          if (report?._id) {
            queryClient.invalidateQueries({
              queryKey: [`fetch-report-activity-history-${report._id}`],
            });
          }

          onClose();
        },
      },
    );
    const { mutate: deleteInventory } = useMutation(deleteInventoryReport, {
      onSuccess: () => {
        onClose();
        toast({
          status: 'success',
          title: 'Report Deleted',
          isClosable: true,
          duration: 3000,
        });

        // Invalidate all related queries to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: ['fetch-reports'],
        });

        queryClient.invalidateQueries({
          queryKey: ['fetch-inventory-reports'],
        });

        // Also invalidate any queries that might contain the specific report
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey.includes('fetch-reports') ||
            query.queryKey.includes('fetch-inventory-reports') ||
            query.queryKey.includes('fetch-impact-reports'),
        });

        // Force refetch the report list with all possible query key variations
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length > 0 &&
              queryKey[0] === 'fetch-reports'
            );
          },
        });

        // Invalidate activity history queries for this specific report
        if (report?._id) {
          queryClient.invalidateQueries({
            queryKey: [`fetch-report-activity-history-${report._id}`],
          });
        }
      },
    });

    const onSubmit = (payload: any) => {
      if (payload) {
        editInventory({
          id: report?._id,
          ...payload,
        });
      }
      // only change to ready from impact if previous status is in review.
      if (
        payload.reportReady &&
        report?.status === InventoryReportStatusEnum.IN_REVIEW
      ) {
        editReportStatus({
          reportId: report!._id,
          status: InventoryReportStatusEnum.READY_FOR_IMPACT,
        });
      }
    };

    const onRefresh = async () => {
      let response = await manualRefreshTriggerById(report!._id);

      if (response.item) setReport(response.item);
    };
    const onDelete = async () => {
      deleteInventory(report!._id);
    };

    return (
      <EditHarvestSummaryModalView
        report={report}
        isLoading={isLoading || isUpdating}
        isOpen={isOpen}
        onRefresh={onRefresh}
        onClose={onClose}
        onSubmit={onSubmit}
        onDelete={onDelete}
        updateStatus={editReportStatus}
      />
    );
  },
);

const EditHarvestSummaryModal = memo(Container);

export default EditHarvestSummaryModal;
